import React, { HTMLProps, useEffect, useRef } from 'react';
import Chart from 'chart.js';
import { toPercentage } from '../../../../utils/parsers';

Chart.defaults.global.defaultFontFamily = 'museo-sans, sans-serif';

type ForecastFundsChartDataSet = Chart.ChartDataSets & {
    id: string;
};

export const FundPieChart: React.FC<
    {
        title?: string;
        sections: { items: { tint: string; name: string; weight: number }[] }[];
    } & HTMLProps<HTMLDivElement>
> = ({ className, sections, title, ...rest }) => {
    const canvasRef = useRef<HTMLCanvasElement>(undefined!);
    const chartRef = useRef<Chart>(null);
    const dataSetRef = useRef<ForecastFundsChartDataSet[]>([]);

    useEffect(() => {
        if (!canvasRef.current) return;

        if (!chartRef.current) {
            chartRef.current = new Chart(canvasRef.current, {
                type: 'pie',
                data: {
                    datasets: dataSetRef.current,
                },
                plugins: [],
                options: {
                    tooltips: {
                        callbacks: {
                            label: function (tooltipItem, data) {
                                if (tooltipItem.datasetIndex !== undefined && tooltipItem.index !== undefined) {
                                    let value =
                                        (data?.datasets?.[tooltipItem.datasetIndex]?.data?.[tooltipItem.index] as
                                            | number
                                            | undefined) || 0;
                                    let label = data?.labels?.[tooltipItem.index] || '';
                                    return `${label}: ${toPercentage(value)}`;
                                }
                                return '';
                            },
                        },
                    },
                    responsive: true,
                    animation: { duration: 0 },
                    maintainAspectRatio: false,
                    legend: {
                        display: false,
                    },
                    cutoutPercentage: 70,
                },
            });
        }

        addLabelsToChart(chartRef.current, sections);
    }, [canvasRef, sections]);
    return (
        <div className={`relative flex-1 ${className}`} {...rest}>
            <canvas ref={canvasRef}></canvas>
            {!!title && (
                <div className="absolute inset-0 pointer-events-none flex items-center justify-center text-bnd-black font-medium text-lg">
                    <div className="flex flex-col items-center">
                        {hasWhiteSpace(title) ? titleWithSpacesToReactNode(title) : title}
                    </div>
                </div>
            )}
        </div>
    );
};

function hasWhiteSpace(s: string) {
    return s.indexOf(' ') >= 0;
}

const titleWithSpacesToReactNode = (title: string) => {
    const splittedTitle = title.split(' ');
    return splittedTitle.map((word, index) => {
        return <div key={word}>{word}</div>;
    });
};

const addLabelsToChart = (chart: Chart, sections: { items: { tint: string; name: string; weight: number }[] }[]) => {
    const allItems = sections.reduce<{ tint: string; name: string; weight: number }[]>((total, section) => {
        return [...total, ...section.items];
    }, []);

    chart.data.labels = [...allItems.map((o) => o.name)];
    chart.data.datasets = [
        {
            data: [...allItems.map((o) => o.weight)],
            backgroundColor: [...allItems.map((fund) => fund.tint)],
        },
    ];

    chart.update();
};
